'use client';

// Third Party
import * as React from 'react';
import { Formik, Form as FormikForm, FormikHelpers } from 'formik';
import { useSearchParams } from 'next/navigation';
import * as Sentry from '@sentry/nextjs';

// Utils
import { useMutation } from '@apollo/client/react';
import { ValidationError, UserCreateDocument } from '@gql';
import { routes } from '@routes';
import { useNotification } from '@contexts/use-notification';

// UI
import { Button } from '@components/button';
import { FormSchema, formSchema, initialValues } from './form-schema';
import { FormikInput } from '@components/form/formik-input';
import { FormikPasswordInput } from '@components/form/formik-password-input';
import { FormikTurnstile } from '@components/form/formik-turnstile';
import { Link } from '@components/link';
import { Typography } from '@components/typography';
import { signIn } from '@lib/auth/client';
interface Props {
  referralCode: string;
  redirectUrl?: string;
}

/**
 * Validates a redirect URL to prevent open redirect vulnerabilities.
 * Only permits relative paths that resolve to the same origin.
 */
const isValidRedirect = (redirect: string): boolean => {
  // Must start with '/' but not '//' (protocol-relative)
  if (!redirect.startsWith('/') || redirect.startsWith('//')) {
    return false;
  }

  // Reject javascript: URLs (case-insensitive check after trimming)
  if (redirect.toLowerCase().includes('javascript:')) {
    return false;
  }

  // Verify the URL resolves to the same origin
  try {
    const url = new URL(redirect, window.location.origin);
    return url.origin === window.location.origin;
  } catch {
    return false;
  }
};
export const Form: React.FC<Props> = ({
  referralCode,
  redirectUrl
}) => {
  const [userCreateMutation] = useMutation(UserCreateDocument);
  const {
    addNotification
  } = useNotification();
  const searchParams = useSearchParams();
  const onSubmit = async ({
    email,
    firstName,
    lastName,
    password,
    captchaToken
  }: FormSchema, formikHelpers: FormikHelpers<FormSchema>) => {
    try {
      const results = await userCreateMutation({
        variables: {
          input: {
            email: email,
            firstName: firstName,
            lastName: lastName,
            password: password,
            referralCode: referralCode,
            captchaToken: captchaToken || undefined
          }
        }
      });
      if (results.data?.userCreate.__typename === 'User') {
        if (!results.data?.userCreate.refreshToken) throw new Error('refreshToken not present');
        await signIn('EngineRefreshTokenLogin', {
          refresh_token: results.data.userCreate.refreshToken,
          redirect: false
        });
        if (referralCode) {
          window.location.replace(`${routes.userAccountSettings}#your-rewards`);
        } else {
          const redirect = redirectUrl || searchParams?.get('redirect');
          if (redirect && isValidRedirect(redirect)) {
            window.location.replace(redirect);
          } else {
            window.location.reload();
          }
        }
      } else if (results.data?.userCreate.__typename && results.data?.userCreate.__typename === 'ValidationErrorCollection') {
        formikHelpers.setErrors(results.data?.userCreate.errors.reduce((obj: {}, item: ValidationError) => {
          if (!item.field) {
            return obj;
          } else {
            return Object.assign(obj, {
              [item.field]: item.message
            });
          }
        }, {}));
        // Clear password values
        formikHelpers.setFieldTouched('password', false, false);
        formikHelpers.setFieldValue('password', '', false);
      }
    } catch (error) {
      Sentry.captureException(error);
      addNotification({
        type: 'error',
        title: 'Account Creation Failed',
        message: 'Something went wrong creating your account. Please try again.'
      });
    } finally {
      formikHelpers.setSubmitting(false);
    }
  };
  return <div>
      <Formik initialValues={initialValues} validationSchema={formSchema} onSubmit={onSubmit}>
        {({
        isSubmitting
      }) => {
        return <FormikForm>
              <div className="grid grid-cols-4 gap-3 sm:gap-4 max-w-2xl mx-auto">
                <div className="col-span-4 sm:col-span-2">
                  <FormikInput type="text" label="First Name" name="firstName" id="firstName" />
                </div>

                <div className="col-span-4 sm:col-span-2">
                  <FormikInput type="text" label="Last Name" name="lastName" id="lastName" />
                </div>
                <div className="col-span-4 mt-1">
                  <Typography component="p" className="text-xs text-center">
                    This should be the same name as on your government ID.
                  </Typography>
                </div>

                <div className="col-span-4">
                  <FormikInput type="email" label="Email" name="email" id="email" />
                </div>

                <div className="col-span-4">
                  <FormikPasswordInput label="Password" name="password" id="password" />

                  <Typography component="p" className="mt-1 sm:mt-2 text-xs text-center">
                    We enforce an 8 character minimum, and suggest avoiding
                    patterns and common words/phrases. See{' '}
                    <Link tabIndex={-1} to="https://support.google.com/accounts/answer/32040?hl=en" target="_blank" rel="noopener noreferrer">
                      Google&apos;s creating a strong password
                    </Link>{' '}
                    guide for more help.
                  </Typography>
                </div>

                <div className="col-span-4">
                  <FormikTurnstile />
                </div>

                <div className="col-span-4 flex flex-col-reverse sm:flex-row justify-center mt-4 sm:mt-6">
                  <Button data-testid="signup-form-button" type="submit" size="lg" isFullWidth={true} disabled={isSubmitting} isLoading={isSubmitting}>
                    <div className="flex justify-center w-full">Sign Up</div>
                  </Button>
                </div>
              </div>
            </FormikForm>;
      }}
      </Formik>
    </div>;
};